import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const Privacy: React.FC = () => {
  useEffect(() => {
    document.title = 'Privacy Policy — Pixano';
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navigation />
      <main className="flex-1">
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <p className="text-sm uppercase tracking-wide text-blue-600 font-semibold">
                Legal
              </p>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
              <p className="text-gray-600 mb-2">
                We care about your privacy. This Privacy Policy explains how Pixano AI LLC (&quot;Pixano&quot;, &quot;we&quot;,
                &quot;us&quot;) collects, uses, shares, and protects information when you use our website, mobile application,
                portals, and related services (collectively, the &quot;Services&quot;).
              </p>
              <p className="text-sm text-gray-500">Last updated: April 2026</p>
            </div>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Summary and Scope</h2>
                <p className="mb-3">
                  This Privacy Policy describes how Pixano collects, uses, shares, and protects information when you use the
                  Services. This policy applies to information we collect through the Services, including our website, mobile
                  application, and user/client portals.
                </p>
                <p>
                  This policy does not apply to information collected by third parties, even if linked from the Services. We
                  encourage you to review the privacy policies of third-party services you interact with.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
                <p className="mb-3">
                  We collect information that you provide directly to us and information that is automatically collected when
                  you use the Services:
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">2.1 Information You Provide</h3>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>
                    <strong>Account Information:</strong> Name, email address, username, password, and other information you
                    provide when creating or updating your account
                  </li>
                  <li>
                    <strong>User Content:</strong> Photos, videos, metadata, annotations, and other content you submit through
                    the Services
                  </li>
                </ul>
                <p className="mb-3 ml-4">
                  User Content is collected at the time you capture, upload, or submit it through the Services.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>
                    <strong>Communications:</strong> Information you provide when contacting us for support, submitting
                    feedback, or communicating with us through the Services
                  </li>
                  <li>
                    <strong>Profile Information:</strong> Optional profile information you choose to provide
                  </li>
                </ul>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">2.2 Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>
                    <strong>Device Information:</strong> Device type, operating system, unique device identifiers, mobile
                    network information
                  </li>
                  <li>
                    <strong>Usage Data:</strong> Information about how you use the Services, including pages visited,
                    features used, time spent, and interactions
                  </li>
                  <li>
                    <strong>Log Data:</strong> IP address, browser type, access times, referring URLs, and other technical
                    information
                  </li>
                  <li>
                    <strong>Location Data:</strong> General location information (e.g., country, region) that may be derived
                    from your IP address or device settings, if you grant location permissions
                  </li>
                  <li>
                    <strong>Cookies and Tracking Technologies:</strong> We may use cookies, web beacons, and similar
                    technologies to collect information about your browsing activities. You can control cookies through your
                    browser settings, though this may affect functionality of the Services
                  </li>
                </ul>
                <p className="mt-3">
                  <strong>Important:</strong> Please do not submit content containing sensitive personal information (such as
                  financial information, health records, government-issued identification, or personal information of others
                  without their consent). We do not permit the submission of sensitive personal information. Content
                  containing such information may be removed when identified.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. How We Use Information</h2>
                <p className="mb-3">We use the information we collect for the following purposes:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>
                    <strong>Provide and Operate Services:</strong> To provide, maintain, operate, and improve the Services,
                    including processing your submissions, managing your account, and delivering features and functionality
                  </li>
                  <li>
                    <strong>Content Moderation and Quality Control:</strong> To review, validate, approve, reject, and
                    moderate User Content to ensure quality, compliance with our Terms, and prevent fraud and abuse
                  </li>
                  <li>
                    <strong>Dataset Creation and Licensing:</strong> To create datasets, collections, and compilations
                    incorporating User Content and derivative works (such as annotations, labels, and metadata), and to
                    license and distribute these datasets to business customers for use in training machine learning models
                    and developing AI systems
                  </li>
                  <li>
                    <strong>Machine Learning and AI Development:</strong> To train, develop, improve, and evaluate machine
                    learning models, artificial intelligence systems, and related technologies using User Content and
                    derivative works
                  </li>
                  <li>
                    <strong>Analytics and Improvement:</strong> To analyze usage patterns, understand how the Services are
                    used, identify trends, and improve our services, features, and user experience
                  </li>
                  <li>
                    <strong>Communication:</strong> To communicate with you about your account, the Services, updates,
                    support requests, and (with your consent) marketing communications
                  </li>
                  <li>
                    <strong>Security and Fraud Prevention:</strong> To detect, prevent, and address security issues, fraud,
                    abuse, and other harmful or illegal activity
                  </li>
                  <li>
                    <strong>Legal Compliance:</strong> To comply with applicable laws, regulations, legal processes, and
                    government requests
                  </li>
                  <li>
                    <strong>Enforcement:</strong> To enforce our Terms of Service and other policies
                  </li>
                  <li>
                    <strong>Rewards Program:</strong> To administer and manage rewards, points, tokens, and other incentives
                  </li>
                </ul>
                <p className="mb-3">We may combine information from different sources to provide and improve the Services.</p>
                <p>
                  By submitting User Content through the Services, and by accepting the in-app disclosure presented during use
                  of the Services, you consent to the processing, use, sharing, and licensing of your User Content, including
                  use in machine learning and AI systems and processing by third-party service providers, as described in this
                  Privacy Policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. How We Share Information</h2>
                <p className="mb-3">
                  We do not sell your personal information (such as name, email, or account details) to third parties. We may
                  share information in the following circumstances:
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.1 Service Providers and Processors</h3>
                <p className="mb-3 ml-4">
                  We may share information with third-party service providers and processors who perform services on our
                  behalf, such as:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-8 mb-3">
                  <li>Cloud hosting and storage providers</li>
                  <li>Analytics and data processing services</li>
                  <li>Content moderation and quality assurance services</li>
                  <li>Payment processing services</li>
                  <li>Customer support and communication services</li>
                </ul>
                <p className="mb-3 ml-4">
                  These service providers are contractually obligated to use information only for the purposes we specify and
                  in accordance with this Privacy Policy.
                </p>
                <h4 className="text-lg font-semibold text-gray-900 mb-2 mt-4 ml-4">AI Processing and Third-Party Services</h4>
                <p className="mb-3 ml-4">
                  We may use third-party service providers, including artificial intelligence and machine learning service
                  providers, to process User Content (such as photos, videos, and associated metadata) for purposes including
                  content validation, labeling, annotation, and quality review.
                </p>
                <p className="mb-3 ml-4">
                  When you submit content through the Services, your User Content is uploaded to Pixano systems and will be
                  transmitted to and processed by these third-party services as part of providing the Services.
                </p>
                <p className="ml-4">
                  These providers are contractually obligated to process User Content only on our behalf and in accordance
                  with this Privacy Policy. We do not share your account information (such as name or email address) with
                  these providers.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.2 Business Customers and Dataset Licensing</h3>
                <p className="mb-3 ml-4">
                  We create datasets, collections, and compilations that incorporate User Content and derivative works (such as
                  annotations, labels, and metadata). These datasets are licensed and distributed to business customers for
                  use in training machine learning models and developing AI systems. Datasets are structured and processed for
                  machine learning purposes and are not intended to identify individual users.
                </p>
                <p className="mb-3 ml-4">
                  <strong>Important:</strong> When we license datasets to business customers, we make reasonable efforts to
                  exclude personal information from those datasets. This depends on the nature of User Content submitted by
                  users.
                </p>
                <p className="mb-3 ml-4">
                  Once datasets have been licensed or distributed to business customers, we may not be able to remove your User
                  Content from those datasets. However, we will make reasonable efforts to cease using your User Content in new
                  datasets and services upon your request, where feasible.
                </p>
                <p className="ml-4">
                  Pixano does not use User Content to identify individual users or for facial recognition or identity
                  verification purposes.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.3 Legal Requirements</h3>
                <p className="ml-4">
                  We may disclose information if required by law, regulation, legal process, or government request, or to
                  protect the rights, property, or safety of Pixano, our users, or others.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.4 Business Transfers</h3>
                <p className="ml-4">
                  In the event of a merger, acquisition, reorganization, or sale of assets, information may be transferred to
                  the acquiring entity or successor.
                </p>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.5 With Your Consent</h3>
                <p className="ml-4">We may share information in other ways with your consent or at your direction.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. Data Retention</h2>
                <p className="mb-3">
                  We retain information for as long as necessary to provide the Services, fulfill the purposes described in this
                  Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>
                    <strong>Account Information:</strong> We retain account information while your account is active and for a
                    reasonable period after account closure, as needed for legal and business purposes
                  </li>
                  <li>
                    <strong>User Content:</strong> We retain User Content and derivative works (annotations, labels, metadata)
                    as needed for our operations, including dataset creation and licensing, and for training and improving ML
                    models and services
                  </li>
                  <li>
                    <strong>Usage and Log Data:</strong> We retain usage and log data for analytics, security, and business
                    purposes, typically for a period consistent with industry standards
                  </li>
                </ul>
                <p className="mb-3">
                  <strong>Important:</strong> If you request deletion of your account or User Content, we will make reasonable
                  efforts to delete your information from our systems. However, we may not be able to delete information that
                  has been incorporated into datasets that have already been licensed or distributed to business customers. We
                  will cease using your information in new datasets and services where feasible.
                </p>
                <p>
                  We may also retain certain information as required by law or for legitimate business purposes, such as
                  fraud prevention, dispute resolution, and enforcement of our Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Your Choices and Rights</h2>
                <p className="mb-3">You have the following choices and rights regarding your information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                  <li>
                    <strong>Access and Update:</strong> You can access and update your account information through your account
                    settings or by contacting us
                  </li>
                  <li>
                    <strong>Deletion:</strong> You can request deletion of your account and User Content by contacting us at{' '}
                    support@pixano.ai. We will make reasonable efforts to delete your information, subject to the limitations
                    described in Section 5 (Data Retention)
                  </li>
                  <li>
                    <strong>Marketing Communications:</strong> You can opt out of marketing emails by following the unsubscribe
                    instructions in those emails or by contacting us. You may still receive transactional and service-related
                    communications
                  </li>
                  <li>
                    <strong>Cookies:</strong> You can control cookies through your browser settings. Note that disabling
                    cookies may affect the functionality of the Services
                  </li>
                  <li>
                    <strong>Location Data:</strong> You can control location permissions through your device settings
                  </li>
                </ul>
                <p>
                  Depending on your jurisdiction, you may have additional rights under applicable data protection laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. Security</h2>
                <p>
                  We implement reasonable administrative, technical, and physical safeguards designed to protect your
                  information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. Children&apos;s Privacy</h2>
                <p>The Services are not intended for children under 13 years of age.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. International Transfers</h2>
                <p>Information may be transferred to and processed in the United States and other countries.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Changes to This Privacy Policy</h2>
                <p>We may update this Privacy Policy from time to time.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. Contact</h2>
                <p>
                  <strong>Email:</strong> support@pixano.ai
                </p>
              </section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
